import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { fetchAnalytics } from '../api/placeholder';

const PIE_COLORS = ['#C0392B', '#DAA520', '#B8922D'];
const BAR_COLORS = ['#D4A843', '#E8C95A', '#B8922D', '#DAA520', '#C49B38'];

const darkTooltipStyle = {
  borderRadius: '12px',
  border: '1px solid #252525',
  backgroundColor: '#141414',
  color: '#e2e8f0',
  fontSize: '13px',
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics()
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-neon-cyan border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Analytics</h1>
          <p className="text-sm text-slate-500">Visual insights into financial wellness engagement</p>
        </div>
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-8 text-center">
          <p className="text-sm text-neon-pink">{error || 'No data available'}</p>
          <p className="mt-2 text-xs text-slate-500">Make sure the n8n workflow is active and listening.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">Analytics</h1>
        <p className="text-sm text-slate-500">
          Visual insights into financial wellness engagement
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Completed Checks Over Time */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-5 lg:col-span-2">
          <h3 className="mb-4 text-sm font-semibold text-neon-cyan/80">
            Completed Financial Checks Over Time
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.completedChecksOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} stroke="#252525" />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} stroke="#252525" />
              <Tooltip contentStyle={darkTooltipStyle} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#D4A843"
                strokeWidth={2.5}
                dot={{ fill: '#D4A843', r: 4, stroke: '#D4A843', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#D4A843', stroke: '#000000', strokeWidth: 3 }}
                name="Checks"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Leads by Category */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-5">
          <h3 className="mb-4 text-sm font-semibold text-neon-cyan/80">
            Leads by Category
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.leadsByCategory}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={4}
                dataKey="count"
                nameKey="category"
                label={({ category, percent }) =>
                  `${category} ${(percent * 100).toFixed(0)}%`
                }
                stroke="#000000"
                strokeWidth={2}
              >
                {data.leadsByCategory.map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={darkTooltipStyle} />
              <Legend
                wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Milestone Distribution */}
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-5">
          <h3 className="mb-4 text-sm font-semibold text-neon-cyan/80">
            Milestone Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.milestoneDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" />
              <XAxis dataKey="milestone" tick={{ fontSize: 11, fill: '#64748b' }} stroke="#252525" />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} stroke="#252525" />
              <Tooltip contentStyle={darkTooltipStyle} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Users">
                {data.milestoneDistribution.map((_, idx) => (
                  <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
